import { isInBrowser, type ApplicationContext } from '@mimir/common/context/applicationContext';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import type { BufferCache } from '@mimir/common/util/bufferCache';
import type { SegmentProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import { exceptionToString } from '@mimir/common/util/exception';
import { CutterItemMediaPlayer } from './cutter/cutterItemMediaPlayer';
import type { VideoPlayerOptions } from './itemMediaPlayer';
import { createItemMediaVideoPlayer } from './itemMediaPlayer';
import type { ItemMediaPlayerOps } from './itemMediaPlayerOps';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';

/**
 * Create an item media player instance.
 *
 * This factory function decides between creating a CutterItemMediaPlayer
 * (advanced frame-accurate player) or a standard ItemMediaVideoPlayer
 * based on feature flags and application context.
 */
export function createItemMediaPlayer(
  services: {
    applicationContext: ApplicationContext;
    bufferCache: BufferCache;
    logger: Logger;
    createSegmentProviderForItem: (itemId: string) => SegmentProvider;
    fetch: BasicFetch;
    apiActions: VideoApiActions;
    itemPollingOps: ItemPollingOps,
  },
  useCutterPlayer: boolean,
  videoPlayerOptions: VideoPlayerOptions,
): ItemMediaPlayerOps {
  const { applicationContext, logger } = services;

  if (useCutterPlayer && contextSupportsCutterPlayer()) {
    try {
      const prematureVideoFrameReuseBugWorkaround =
        isInBrowser(applicationContext) ? applicationContext.prematureVideoFrameReuseBugWorkaround : false;
      return new CutterItemMediaPlayer(services, { prematureVideoFrameReuseBugWorkaround });
    } catch (err) {
      logger.warn(`Failed to initialize cutter player: ${exceptionToString(err)}`);
    }
  }
  return createItemMediaVideoPlayer(services, videoPlayerOptions);
}

/**
 * Check if the current environment supports the CutterItemMediaPlayer.
 *
 * The CutterItemMediaPlayer requires:
 * - Web Workers (for video decoding)
 * - Canvas API (for video rendering)
 * - AudioContext (for audio playback)
 * - AudioWorklet (for low-latency audio processing)
 * - Document API (for creating DOM elements)
 */
let _cutterPlayerSupportCache: boolean | null = null;

// we only need to check context support once, so we cache the result.
function contextSupportsCutterPlayer(): boolean {
  if (_cutterPlayerSupportCache !== null) {
    return _cutterPlayerSupportCache;
  }
  _cutterPlayerSupportCache = contextSupportsCutterPlayerCheck();
  return _cutterPlayerSupportCache;
}

function contextSupportsCutterPlayerCheck(): boolean {
  try {
    if (typeof Worker === 'undefined') {
      return false;
    }

    if (typeof HTMLCanvasElement === 'undefined') {
      return false;
    }

    if (typeof document === 'undefined' || !document.createElement) {
      return false;
    }

    if (typeof window === 'undefined' || typeof window.devicePixelRatio === 'undefined') {
      return false;
    }

    if (typeof AudioContext === 'undefined') {
      return false;
    }

    if (typeof AudioDecoder === 'undefined') {
      return false;
    }

    // AudioWorklet support (more specific than just AudioContext)
    const testContext = new AudioContext();
    const hasAudioWorklet = testContext.audioWorklet !== undefined;
    void testContext.close();

    if (!hasAudioWorklet) {
      return false;
    }

    // AudioWorkletNode support
    if (typeof AudioWorkletNode === 'undefined') {
      return false;
    }

    return true;
  } catch (err) {
    // If any of the checks throw an error, the environment doesn't support
    // the required APIs
    return false;
  }
}
